/**
 * ConfigPage.tsx — Mari's skill config registry admin panel (story
 * E0-BO-CFG-SKILL-REGISTRY-ADMIN; selectors per E0-BO-CFG-S3; filters/sort/
 * pagination/activate-any-version/agent_key per
 * E0-BO-CFG-ACTIVATE-EXISTING-VERSION-AND-AGENT-CONFIG-EDITOR).
 *
 * Browse: two correlated selectors (skill, product) populated on load from
 * `GET /api/config/skills/keys`, then `GET /api/config/skills` — paginated
 * (limit/offset/total), filtered (is_active, config_version_prefix), sorted
 * (created_at asc/desc) — for that ONE skill/product pair. The list route
 * requires `skill_name` as a required query param alongside product_code
 * (verified against `services/backoffice/app/routes/skill_config.py` on
 * PR #534/#540/#641); there is no cross-skill browse. `tenant_id` is never a
 * client concept — the BFF fills it server-side from
 * `BackofficeConfig.capital_service_tenant_id`.
 * Compare: tick exactly two rows, see what changed (VersionCompareView — a
 * plain side-by-side, no diff library). Edit: pick a base version to edit
 * its prompt copy and publish it (SkillVersionEditForm) — creating and
 * activating are two separate, deliberate actions there. Activate: any
 * listed non-active version can be activated (= rolled back to) directly
 * from its row, via the SAME review/confirm dialog as the edit form
 * (ActivateVersionControl) — no need to open the editor first.
 */

import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import type { SkillKey } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { VersionCompareView } from './VersionCompareView';
import { SkillVersionEditForm } from './SkillVersionEditForm';
import { ActivateVersionControl } from './ActivateVersionControl';
import { useSkillKeys } from './useSkillKeys';
import { useSkillVersions } from './useSkillVersions';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { formatDateTime } from '../leads/formatDateTime';
import floating from '../../lib/floating';

export interface ConfigPageProps {
  apiClient: ApiClient;
}

type Mode = 'browse' | 'compare' | 'edit';
type ActiveFilter = 'all' | 'active' | 'inactive';
type SortOrder = 'created_at_desc' | 'created_at_asc';

const PAGE_SIZE = 20;

/** Identifies a row: (skill_name, config_version) is the key, per the contract. */
const rowKey = (skillName: string, configVersion: string) => `${skillName}::${configVersion}`;

/**
 * DOM-only stand-in for a `null` product_code in the `<select>` value. Never
 * touches application state — `selectedKey.product_code` stays a real
 * `string | null` throughout; only this one rendering boundary needs a
 * string. Distinct from both `"null"` and `""` so it can never collide with
 * a real product code or the placeholder option.
 */
const NO_PRODUCT_OPTION = '__no_product__';
const productSelectValue = (code: string | null) => code ?? NO_PRODUCT_OPTION;
const BackButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button
    type="button"
    data-testid="config-back"
    onClick={onClick}
    className="inline-flex w-fit items-center gap-1 rounded max-md:min-h-11 text-meta text-gold underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-outline-focus"
  >
    <span aria-hidden="true">←</span> {label}
  </button>
);

export const ConfigPage = ({ apiClient }: ConfigPageProps) => {
  const { t, i18n } = useTranslation();
  const keysState = useSkillKeys(apiClient);
  const { items, total, phase, load } = useSkillVersions(apiClient);

  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<SkillKey | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<Mode>('browse');
  const [editTarget, setEditTarget] = useState<{ skillName: string; configVersion: string } | null>(
    null,
  );

  // Filter / sort / pagination state (E0-BO-CFG-ACTIVATE-EXISTING-VERSION).
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [versionPrefix, setVersionPrefix] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('created_at_desc');
  const [offset, setOffset] = useState(0);

  const skillNames = useMemo(
    () => [...new Set(keysState.keys.map((key) => key.skill_name))],
    [keysState.keys],
  );
  const productsForSkill = useMemo(
    () => keysState.keys.filter((key) => key.skill_name === selectedSkill),
    [keysState.keys, selectedSkill],
  );

  const buildQuery = (key: SkillKey, offsetValue: number) => ({
    skill_name: key.skill_name,
    product_code: key.product_code,
    is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
    config_version_prefix: versionPrefix.trim() === '' ? undefined : versionPrefix.trim(),
    sort: sortOrder,
    limit: PAGE_SIZE,
    offset: offsetValue,
  });

  // Unambiguous skill (one skill total, or a single overall pair): preselect
  // it so the operator only ever faces a product choice, never a skill
  // choice of one.
  useEffect(() => {
    if (keysState.phase !== 'ready') return;
    if (skillNames.length === 1) setSelectedSkill(skillNames[0]!);
  }, [keysState.phase, skillNames]);

  // Recompute the default product whenever the chosen skill changes (auto or
  // manual): a single product for that skill preselects it; more than one
  // requires an explicit choice. Runs only on a SKILL change, so it never
  // clobbers a manual product pick made under the same skill.
  useEffect(() => {
    if (selectedSkill == null) {
      setSelectedKey(null);
      return;
    }
    const options = keysState.keys.filter((key) => key.skill_name === selectedSkill);
    setSelectedKey(options.length === 1 ? options[0]! : null);
  }, [selectedSkill, keysState.keys]);

  // A full (skill, product) selection — or a filter/sort change under an
  // already-selected pair — loads page one of the version list. Pagination
  // itself (Prev/Next) is imperative (goToOffset below), never routed
  // through this effect, so it never gets silently reset mid-browse.
  useEffect(() => {
    if (selectedKey == null) return;
    setSelected(new Set());
    setMode('browse');
    setOffset(0);
    floating(load(buildQuery(selectedKey, 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedKey?.skill_name,
    selectedKey?.product_code,
    activeFilter,
    versionPrefix,
    sortOrder,
    load,
  ]);

  const handleSkillChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setSelectedSkill(value === '' ? null : value);
  };

  const handleProductChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (value === '') {
      setSelectedKey(null);
      return;
    }
    const match = productsForSkill.find((key) => productSelectValue(key.product_code) === value);
    if (match) setSelectedKey(match);
  };

  const refreshList = () => {
    if (selectedKey == null) return;
    floating(load(buildQuery(selectedKey, offset)));
  };

  const goToOffset = (nextOffset: number) => {
    if (selectedKey == null) return;
    setOffset(nextOffset);
    floating(load(buildQuery(selectedKey, nextOffset)));
  };

  if (keysState.phase === 'unauthorized' || phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  const selectedRows = [...selected].map((key) => {
    const [skillName, configVersion] = key.split('::');
    return { skillName: skillName!, configVersion: configVersion! };
  });

  // Downstream compare/edit take a plain string productCode (their own,
  // frozen contract — story E0-BO-CFG-S3 does not touch it). A null product
  // only ever reaches this boundary for a registry entry that has no
  // versions to browse in the first place (list/detail/create/activate all
  // require a real product_code upstream), so compare/edit are unreachable
  // for it in practice.
  const editScopeProductCode = selectedKey?.product_code ?? '';

  const renderBranch1 = () => {
    if (keysState.phase === 'loading')
      return (
        <p role="status" data-testid="config-keys-loading" className="text-body text-ink-2">
          {t('config.scope.loading')}
        </p>
      );
    if (keysState.phase === 'error')
      return (
        <p role="alert" data-testid="config-keys-error" className="text-body text-status-down">
          {t('config.scope.error')}
        </p>
      );
    if (keysState.phase === 'empty')
      return (
        <p data-testid="config-keys-empty" className="text-body text-ink-2">
          {t('config.scope.empty')}
        </p>
      );
    const renderBranch2 = () => {
      if (mode === 'compare' && selectedRows.length === 2 && selectedKey != null)
        return (
          <>
            <BackButton onClick={() => setMode('browse')} label={t('config.back')} />
            <VersionCompareView
              apiClient={apiClient}
              left={{
                skillName: selectedRows[0]!.skillName,
                configVersion: selectedRows[0]!.configVersion,
                productCode: editScopeProductCode,
              }}
              right={{
                skillName: selectedRows[1]!.skillName,
                configVersion: selectedRows[1]!.configVersion,
                productCode: editScopeProductCode,
              }}
            />
          </>
        );
      if (mode === 'edit' && editTarget != null && selectedKey != null)
        return (
          <>
            <BackButton onClick={() => setMode('browse')} label={t('config.back')} />
            <SkillVersionEditForm
              apiClient={apiClient}
              base={{
                skillName: editTarget.skillName,
                configVersion: editTarget.configVersion,
                productCode: editScopeProductCode,
              }}
              onActivated={refreshList}
            />
          </>
        );
      const renderBranch20 = () => {
        if (phase === 'loading')
          return (
            <p role="status" data-testid="config-loading" className="text-body text-ink-2">
              {t('config.loading')}
            </p>
          );
        if (phase === 'error')
          return (
            <p role="alert" data-testid="config-error" className="text-body text-status-down">
              {t('config.errors.list')}
            </p>
          );
        if (selectedKey == null)
          return (
            <p data-testid="config-no-scope" className="text-body text-ink-2">
              {t('config.scope.hint')}
            </p>
          );
        if (items.length === 0)
          return (
            <p data-testid="config-empty" className="text-body text-ink-2">
              {t('config.empty')}
            </p>
          );
        const versionColumns: DataTableColumn<(typeof items)[number]>[] = [
          {
            key: 'select',
            header: <span className="sr-only">{t('config.compare.selectHint')}</span>,
            renderCell: (item) => {
              const key = rowKey(item.skill_name, item.config_version);
              const checked = selected.has(key);
              return (
                <input
                  type="checkbox"
                  data-testid={`config-select-${key}`}
                  checked={checked}
                  disabled={!checked && selected.size >= 2}
                  onChange={() =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (next.has(key)) {
                        next.delete(key);
                      } else if (next.size < 2) {
                        next.add(key);
                      }
                      return next;
                    })
                  }
                  aria-label={t('config.compare.selectRow', {
                    skill: item.skill_name,
                    version: item.config_version,
                  })}
                  className="h-6 w-6 rounded border-ink-3 bg-surface-1 text-gold focus-visible:ring-2 focus-visible:ring-outline-focus"
                />
              );
            },
          },
          {
            key: 'skill',
            header: t('config.columns.skill'),
            renderCell: (item) => <span className="text-ink-1">{item.skill_name}</span>,
          },
          {
            key: 'version',
            header: t('config.columns.version'),
            renderCell: (item) => <span className="text-ink-1">{item.config_version}</span>,
          },
          {
            key: 'active',
            header: t('config.columns.active'),
            renderCell: (item) =>
              item.is_active ? (
                <Badge
                  tone="ok"
                  data-testid={`config-active-${rowKey(item.skill_name, item.config_version)}`}
                >
                  {t('config.active')}
                </Badge>
              ) : (
                <span className="text-meta text-ink-3">{t('config.inactive')}</span>
              ),
          },
          {
            key: 'agentKey',
            header: t('config.columns.agentKey'),
            renderCell: (item) => (
              <span
                className="text-ink-2"
                data-testid={`config-agent-key-${rowKey(item.skill_name, item.config_version)}`}
              >
                {item.agent_key ?? t('config.noAgent')}
              </span>
            ),
          },
          {
            key: 'created',
            header: (
              <button
                type="button"
                data-testid="config-sort-created"
                onClick={() =>
                  setSortOrder((prev) =>
                    prev === 'created_at_desc' ? 'created_at_asc' : 'created_at_desc',
                  )
                }
                aria-label={
                  sortOrder === 'created_at_desc'
                    ? t('config.sort.createdAscLabel')
                    : t('config.sort.createdDescLabel')
                }
                className="inline-flex min-h-6 items-center gap-1 rounded font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
              >
                {t('config.columns.created')}
                <span aria-hidden="true">{sortOrder === 'created_at_desc' ? '↓' : '↑'}</span>
              </button>
            ),
            renderCell: (item) => (
              <span className="text-ink-2">{formatDateTime(item.created_at, i18n.language)}</span>
            ),
          },
          {
            key: 'edit',
            header: (
              <span className="sr-only">{t('config.edit.heading', { skill: '', base: '' })}</span>
            ),
            renderCell: (item) => (
              <button
                type="button"
                data-testid={`config-edit-${rowKey(item.skill_name, item.config_version)}`}
                onClick={() => {
                  setEditTarget({ skillName: item.skill_name, configVersion: item.config_version });
                  setMode('edit');
                }}
                className="inline-flex min-h-11 items-center rounded text-gold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
              >
                {t('config.editFrom')}
              </button>
            ),
          },
          {
            key: 'activate',
            header: <span className="sr-only">{t('config.edit.activate')}</span>,
            renderCell: (item) => (
              <ActivateVersionControl
                apiClient={apiClient}
                skillName={item.skill_name}
                configVersion={item.config_version}
                productCode={editScopeProductCode}
                isActive={item.is_active}
                onActivated={refreshList}
                testIdSuffix={`-${rowKey(item.skill_name, item.config_version)}`}
              />
            ),
          },
        ];
        return (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="config-compare-selected"
                disabled={selected.size !== 2}
                onClick={() => setMode('compare')}
                className="min-h-11"
              >
                {t('config.compare.cta')}
              </Button>
              <p className="text-meta text-ink-3">{t('config.compare.selectHint')}</p>
            </div>

            <DataTable
              data-testid="config-table"
              rowTestId="config-row"
              caption={t('config.tableLabel')}
              rows={items}
              rowKey={(item) => rowKey(item.skill_name, item.config_version)}
              columns={versionColumns}
            />

            <div className="flex items-center justify-between gap-3">
              <p data-testid="config-pagination-summary" className="text-meta text-ink-3">
                {t('config.pagination.summary', {
                  from: offset + 1,
                  to: Math.min(offset + PAGE_SIZE, total),
                  total,
                })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="config-pagination-prev"
                  disabled={offset === 0}
                  onClick={() => goToOffset(Math.max(0, offset - PAGE_SIZE))}
                  className="inline-flex items-center rounded-lg bg-surface-2 px-3 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1 disabled:opacity-50"
                >
                  {t('config.pagination.previous')}
                </button>
                <button
                  type="button"
                  data-testid="config-pagination-next"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => goToOffset(offset + PAGE_SIZE)}
                  className="inline-flex items-center rounded-lg bg-surface-2 px-3 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1 disabled:opacity-50"
                >
                  {t('config.pagination.next')}
                </button>
              </div>
            </div>
          </>
        );
      };

      return (
        <>
          {selectedKey != null ? (
            <fieldset
              data-testid="config-filters-fieldset"
              className="flex flex-wrap items-end gap-3 rounded-xl bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/10"
            >
              <legend className="sr-only">{t('config.filters.legend')}</legend>
              <div className="flex flex-col gap-1">
                <label htmlFor="config-filter-active" className="text-meta text-ink-2">
                  {t('config.filters.status')}
                </label>
                <select
                  id="config-filter-active"
                  data-testid="config-filter-active"
                  value={activeFilter}
                  onChange={(event) => setActiveFilter(event.target.value as ActiveFilter)}
                  className="rounded-md bg-surface-1 px-3 py-1.5 text-body text-ink-1 ring-1 ring-inset ring-ink-3/20 focus:outline-none focus:ring-2 focus:ring-outline-focus"
                >
                  <option value="all">{t('config.filters.statusAll')}</option>
                  <option value="active">{t('config.filters.statusActive')}</option>
                  <option value="inactive">{t('config.filters.statusInactive')}</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="config-filter-prefix" className="text-meta text-ink-2">
                  {t('config.filters.versionPrefix')}
                </label>
                <input
                  id="config-filter-prefix"
                  data-testid="config-filter-prefix"
                  type="text"
                  value={versionPrefix}
                  onChange={(event) => setVersionPrefix(event.target.value)}
                  placeholder={t('config.filters.versionPrefixPlaceholder')}
                  className="rounded-md bg-surface-1 px-3 py-1.5 text-body text-ink-1 ring-1 ring-inset ring-ink-3/20 focus:outline-none focus:ring-2 focus:ring-outline-focus"
                />
              </div>
            </fieldset>
          ) : null}

          {renderBranch20()}
        </>
      );
    };

    return (
      <>
        <fieldset
          data-testid="config-scope-fieldset"
          className="flex flex-wrap items-end gap-3 rounded-xl bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/10"
        >
          <legend className="sr-only">{t('config.scope.legend')}</legend>
          <div className="flex flex-col gap-1">
            <label htmlFor="config-skill" className="text-meta text-ink-2">
              {t('config.scope.skill')}
            </label>
            <select
              id="config-skill"
              data-testid="config-scope-skill"
              value={selectedSkill ?? ''}
              onChange={handleSkillChange}
              className="rounded-md bg-surface-1 px-3 py-1.5 text-body text-ink-1 ring-1 ring-inset ring-ink-3/20 focus:outline-none focus:ring-2 focus:ring-outline-focus"
            >
              <option value="">{t('config.scope.selectPlaceholder')}</option>
              {skillNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="config-product" className="text-meta text-ink-2">
              {t('config.scope.product')}
            </label>
            <select
              id="config-product"
              data-testid="config-scope-product"
              value={selectedKey != null ? productSelectValue(selectedKey.product_code) : ''}
              onChange={handleProductChange}
              disabled={selectedSkill == null}
              className="rounded-md bg-surface-1 px-3 py-1.5 text-body text-ink-1 ring-1 ring-inset ring-ink-3/20 focus:outline-none focus:ring-2 focus:ring-outline-focus disabled:opacity-60"
            >
              <option value="">{t('config.scope.selectPlaceholder')}</option>
              {productsForSkill.map((key) => (
                <option
                  key={productSelectValue(key.product_code)}
                  value={productSelectValue(key.product_code)}
                >
                  {key.product_code ?? t('config.scope.noProduct')}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        {renderBranch2()}
      </>
    );
  };

  return (
    <section aria-labelledby="config-heading" className="flex flex-col gap-5">
      <h1 id="config-heading" className="text-h1 text-ink-1">
        {t('config.title')}
      </h1>

      {renderBranch1()}
    </section>
  );
};
